// Enums
export enum ScoutMessageStatus {
  SENT = 'sent',
  DELIVERED = 'delivered',
  READ = 'read',
  REPLIED = 'replied',
}

export enum TemplateVariable {
  STUDENT_NAME = '{{student_name}}',
  COMPANY_NAME = '{{company_name}}',
  SENDER_NAME = '{{sender_name}}',
}

// Request Types
export interface SendScoutMessageRequest {
  companyId?: number; // 企業ID（APIパス用）
  recipientUserId: number;
  subject: string;
  messageBody: string;
  templateId?: number;
  sendImmediately?: boolean;
  saveAsTemplate?: {
    templateName: string;
    saveTemplate: boolean;
  };
}

export interface BulkSendScoutMessageRequest {
  recipientUserIds: number[];
  subject: string;
  messageBody: string;
  templateId?: number;
  staggerSending?: boolean;
  staggerIntervalMinutes?: number;
}

// Response Types
export interface ScoutMessageResult {
  id: number;
  recipientUserId: number;
  recipientName: string;
  subject: string;
  status: ScoutMessageStatus;
  sentAt: string;
  readAt?: string;
  repliedAt?: string;
  templateUsed?: string;
}

export interface SendScoutMessageResponse {
  success: true;
  data: {
    messageId: number;
    status: 'sent' | 'queued';
    recipient: {
      id: number;
      displayName: string;
    };
    quotaRemaining: {
      today: number;
      thisMonth: number;
    };
    estimatedDelivery: string;
  };
}

export interface BulkSendScoutMessageResponse {
  success: true;
  data: {
    jobId: string;
    totalRecipients: number;
    eligibleRecipients: number;
    estimatedCompletion: string;
    ineligibleRecipients: Array<{
      userId: number;
      reason: string;
    }>;
  };
}

export interface PaginationInfo {
  page: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface ScoutHistoryResponse {
  success: true;
  data: {
    messages: ScoutMessageResult[];
    pagination: PaginationInfo;
    stats: {
      totalSent: number;
      readRate: number;
      replyRate: number;
      quotaUsage: {
        today: { used: number; limit: number };
        thisMonth: { used: number; limit: number };
      };
    };
  };
}

export interface ScoutTemplate {
  id: number;
  name: string;
  subjectTemplate: string;
  bodyTemplate: string;
  variables?: Record<string, string>;
  usageCount: number;
  isActive: boolean;
}

export interface ScoutTemplatesResponse {
  success: true;
  data: {
    templates: ScoutTemplate[];
  };
}

export interface QuotaInfo {
  used: number;
  limit: number;
  resetsAt: string;
}

export interface ScoutQuotaResponse {
  success: true;
  data: {
    currentUsage: {
      perMinute?: QuotaInfo;
      perHour?: QuotaInfo;
      perDay?: QuotaInfo;
      perMonth?: QuotaInfo;
    };
    nextAvailableSend?: string;
    recommendations: string[];
  };
}

// UI Types
export interface MessageQualityScore {
  overall: number;
  personalization: number;
  appropriateness: number;
  clarity: number;
  length: number;
  spamRisk: number;
}

export interface ScoutComposerState {
  recipient?: {
    id: number;
    name: string;
    university?: string;
    faculty?: string;
  };
  subject: string;
  messageBody: string;
  selectedTemplateId?: number;
  qualityScore?: MessageQualityScore;
  isValid: boolean;
}

// スカウトメッセージ詳細レスポンス
export interface ScoutMessageDetailResponse {
  success: true;
  data: {
    message: {
      id: number;
      recipient: {
        id: number;
        name: string;
        email?: string;
        university?: string;
        faculty?: string;
        graduation_year?: number;
      };
      sender: {
        name: string;
        title?: string;
      };
      company: {
        id: number;
        name: string;
      };
      subject: string;
      message_body: string;
      status: ScoutMessageStatus;
      sent_at: string;
      read_at?: string;
      replied_at?: string;
    };
    replies: MessageReplyItem[];
    stats: {
      total_replies: number;
      is_read: boolean;
      is_replied: boolean;
    };
  };
}

export interface MessageReplyItem {
  id: number;
  sender_type: 'student' | 'company';
  sender_name: string;
  message_body: string;
  additional_question?: string;
  reply_type?: string;
  sent_at: string;
  read_at?: string;
}
